import type PublicTransition from '@ember/routing/transition';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { OnboardingLeave } from '@databox/types/domains/ai/chat';

import type { Service } from 'webapp-types';

import GenieThreadBaseController from 'webapp/controllers/genie-thread-base';

export default class GenieChatController extends GenieThreadBaseController {
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('user-settings') declare userSettingsService: Service.UserSettings;

	@tracked onboardingLeave: OnboardingLeave | null = null;
	// `isOnboardingChat` stays set all visit; this tells the onboarding thread from later ones.
	onboardingThreadId: string | null = null;

	// Genie answers the onboarding prompt slowly and users click away.
	// The answer is saved before the retry, so the retried transition falls through.
	handleWillTransition(transition: PublicTransition): void {
		// The saved setting outlives a reload; the in-memory id covers users it isn't saved for.
		const onboardingThreadId =
			this.userSettingsService.onboardingChatThreadId ?? this.onboardingThreadId;

		if (
			!onboardingThreadId ||
			this.aiChatService.urlThreadId !== onboardingThreadId ||
			this.userSettingsService.hasSeenOnboardingChatLeaveConfirm
		) {
			return;
		}

		// Thread URL sync and switching threads stay inside the chat.
		if (transition.to?.name.startsWith('genie.chat')) {
			return;
		}

		transition.abort();

		// Stay, close, Escape and an outside click all answer through onStay.
		const answer = (): void => {
			this.userSettingsService.hasSeenOnboardingChatLeaveConfirm = true;
			this.onboardingLeave = null;
		};

		this.onboardingLeave = {
			onStay: answer,
			onLeave: () => {
				answer();
				transition.retry();
			},
		};
	}

	override reset(): void {
		super.reset();
		this.onboardingLeave = null;
		this.onboardingThreadId = null;
	}
}
